import { IconChevronRight } from '@tabler/icons-react'
import { ButtonHTMLAttributes, ReactNode, useId, useState } from 'react'

import { AstraIcon } from '../AstraIcon/AstraIcon'
import { AstraButton } from '../Inputs/AstraButton/AstraButton'
import { astraTwMerge } from '../Utilities/astraTwMerge'

export type AccordionItemProps<T extends string> = {
    value: T
    title: string
    content: ReactNode
    afterTitle?: ReactNode
    triggerIcon?: ReactNode
    hide?: boolean
    overflow?: boolean
}

type Props<T extends string> = {
    items: AccordionItemProps<T>[]
    itemSeparator?: 'gap' | 'line'
    defaultOpen?: Array<T>
    value?: Array<T>
    onValueChange?: (value: Array<T>) => void
}

type AccordionItemComponentProps<T extends string> = {
    item: AccordionItemProps<T>
    isOpen: boolean
    onToggle: () => void
    itemSeparator: 'gap' | 'line'
}

const AccordionItem = <T extends string>({ item, isOpen, onToggle, itemSeparator }: AccordionItemComponentProps<T>) => {
    const id = useId()
    const triggerId = `accordion-trigger-${id}`
    const contentId = `accordion-content-${id}`
    const state = isOpen ? 'open' : 'closed'

    if (item.hide) {
        return null
    }

    return (
        <div className={astraTwMerge('flex flex-col gap-2', itemSeparator === 'line' ? 'p-4' : '', item.overflow ? 'overflow-auto' : '')}>
            <div className="flex items-center gap-2">
                <div id={triggerId} data-state={state} className="group">
                    <AstraButton
                        variant="ghost"
                        icon={
                            <div className="transition-transform duration-300 ease-in-out group-data-[state=open]:rotate-90">
                                <AstraIcon icon={IconChevronRight} aria-hidden />
                            </div>
                        }
                        label={item.title}
                        endIcon={item.triggerIcon}
                        tightPadding
                        onClick={onToggle}
                        {...({ 'aria-expanded': isOpen, 'aria-controls': contentId } as ButtonHTMLAttributes<HTMLButtonElement>)}
                    />
                </div>
                {item.afterTitle}
            </div>
            <div
                id={contentId}
                role="region"
                aria-labelledby={triggerId}
                hidden={!isOpen}
                className={astraTwMerge('pl-2', item.overflow ? 'overflow-auto' : '')}
            >
                {item.content}
            </div>
        </div>
    )
}

export const AstraAccordion = <T extends string>({ items, itemSeparator = 'line', defaultOpen, value, onValueChange }: Props<T>) => {
    const [internalOpen, setInternalOpen] = useState<Array<T>>(defaultOpen ?? [])
    const isControlled = value !== undefined
    const openItems = isControlled ? value : internalOpen

    const toggleItem = (itemValue: T) => {
        const newOpen = openItems.includes(itemValue) ? openItems.filter(v => v !== itemValue) : [...openItems, itemValue]

        if (isControlled) {
            onValueChange?.(newOpen)
        } else {
            setInternalOpen(newOpen)
            onValueChange?.(newOpen)
        }
    }

    return (
        <div data-test="hero-accordion" className={astraTwMerge('flex flex-col overflow-auto', itemSeparator === 'line' ? 'divide-y divide-base' : 'gap-6')}>
            {items.map((item, index) => (
                <AccordionItem
                    key={index}
                    item={item}
                    isOpen={openItems.includes(item.value)}
                    onToggle={() => toggleItem(item.value)}
                    itemSeparator={itemSeparator}
                />
            ))}
        </div>
    )
}
